import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Museum from './Components/Museum/Museum';
import StartScreen from './Components/StartScreen/StartScreen';
import './App.scss';
import { Hall } from './Components/Hall/Hall';
import { epochs } from './EpochsData/Epochs';
import EpochsMinimap from './Components/EpochsMinimap/EpochsMinimap';
import { Preloader } from './Components/Preloader/Preloader';
import { MinimapProvider, useMinimap } from './contexts/MinimapContext';
import { TransitionProvider, useTransition } from './contexts/TransitionContext';
import Music from './Components/Music/Music';
import { MusicProvider, useMusic } from './contexts/MusicContext';
import { HelmetProvider } from 'react-helmet-async';
import MetaTags from './Components/MetaTags/MetaTags';
import { TimelineEvent } from './Components/Timeline/Timeline';

function AppContent() {
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [currentContent, setCurrentContent] = useState<JSX.Element | null>(null);
  const [activeEvent, setActiveEvent] = useState<TimelineEvent | null>(null);
  const { isPlaying } = useMusic();
  const location = useLocation();
  const { showMinimap, closeMinimap } = useMinimap();
  const { isPageTransitioning, setIsPageTransitioning, isSceneLoaded, setIsSceneLoaded } = useTransition();

  const handleStartScreenHidden = useCallback(() => {
    setShowStartScreen(false);
  }, []);

  useEffect(() => {
  }, [isPageTransitioning, isSceneLoaded]);

  useEffect(() => {
    localStorage.removeItem('minimapVisible');
  }, []);

  useEffect(() => {
    setIsSceneLoaded(false);
    
    const showPreloaderTimer = setTimeout(() => {
      setCurrentContent(
        <Routes location={location}>
          <Route path="/" element={
            <Hall
              isSceneLoaded={isSceneLoaded}
              setIsSceneLoaded={setIsSceneLoaded}
            />
          } />
          <Route path="/museum" element={
            <Museum
              isSceneLoaded={isSceneLoaded}
              setIsSceneLoaded={setIsSceneLoaded}
              onActiveEventChange={setActiveEvent}
            />
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      );
    }, 500);

    return () => {
      clearTimeout(showPreloaderTimer);
    };
  }, [location.pathname]);

  return (
    <div className="App">
      <MetaTags activeEvent={activeEvent} />
      {isPageTransitioning && (
        <Preloader
          isSceneLoaded={isSceneLoaded}
          onFinish={() => setIsPageTransitioning(false)}
          isTransition={true}
        />
      )}
      {showStartScreen && (
        <StartScreen
          isSceneLoaded={isSceneLoaded}
          onHidden={handleStartScreenHidden}
        />
      )}
      <EpochsMinimap
        epochs={epochs}
        isVisible={showMinimap}
        onClose={closeMinimap}
      />
      {isPlaying && <Music />}
      {currentContent}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <TransitionProvider>
          <MinimapProvider>
            <MusicProvider>
              <AppContent />
            </MusicProvider>
          </MinimapProvider>
        </TransitionProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;